import {
  DEFAULT_DATE_FORMAT_FNS,
  FilterKeys,
  GetGroupsQueryFilters,
  GROUPS_ARRANGE_BY_FILTER_KEYS,
} from '@schooly/api';
import {
  getSelectedItemsWithGrouping,
  SelectedItem,
  SelectedItemWithGrouping,
} from '@schooly/components/filters';
import { SchoolUserRole } from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useCheckSchoolSettings } from '../../hooks/useCheckSchoolSettings';

export type UseGroupsFiltersProps = {
  schoolId: string;
  filters: GetGroupsQueryFilters;
  onSetFilters: (filters: GetGroupsQueryFilters) => void;
};

export const useGroupsFilters = ({ schoolId, filters, onSetFilters }: UseGroupsFiltersProps) => {
  const ageGroups = useAgeGroups({
    schoolId: schoolId,
    userType: SchoolUserRole.Student,
  });
  const { getAgeGroupById, schoolLevelsWithAgeGroupsMap } = ageGroups;
  const { hasHouses, hasSubjects } = useCheckSchoolSettings();
  const { formatMessage } = useIntl();

  const actions = useMemo(() => {
    const updateFilter =
      (key: FilterKeys.AgeGroup | FilterKeys.Subject | FilterKeys.Staff) => (v: string[]) => {
        onSetFilters({
          ...filters,
          [key]: v,
        });
      };

    const clearFilter = (key: keyof typeof filters) => () => {
      onSetFilters({
        ...filters,
        [key]: [],
      });
    };

    const onSetOnlyTutorGroups = (value: boolean) => {
      onSetFilters({
        ...filters,
        [FilterKeys.OnlyTutorGroups]: value ? ['1'] : undefined,
        [FilterKeys.Subject]: value ? undefined : filters.subject,
      });
    };

    return {
      onSetDate: (v: [Date, Date]) =>
        onSetFilters({
          ...filters,
          [FilterKeys.Date]: [
            format(v[0], DEFAULT_DATE_FORMAT_FNS),
            format(v[1], DEFAULT_DATE_FORMAT_FNS),
          ],
        }),
      onSetAgeGroup: updateFilter(FilterKeys.AgeGroup),
      onClearAgeGroup: clearFilter(FilterKeys.AgeGroup),
      onSetSubjectId: updateFilter(FilterKeys.Subject),
      onClearSubject: clearFilter(FilterKeys.Subject),
      onSetStaffId: updateFilter(FilterKeys.Staff),
      onClearStaff: clearFilter(FilterKeys.Staff),
      onSetStudentStaffStatusIds: ({
        studentIds,
        staffIds,
      }: {
        studentIds?: string[];
        staffIds?: string[];
      }) => {
        onSetFilters({
          ...filters,
          [FilterKeys.StudentStatus]: studentIds ?? filters[FilterKeys.StudentStatus],
          [FilterKeys.StaffStatus]: staffIds ?? filters[FilterKeys.StaffStatus],
        });
      },
      onClearStatuses: () => {
        onSetFilters({
          ...filters,
          [FilterKeys.StudentStatus]: [],
          [FilterKeys.StaffStatus]: [],
        });
      },
      onSetStudentStaffHouseIds: ({
        studentIds,
        staffIds,
      }: {
        studentIds?: string[];
        staffIds?: string[];
      }) => {
        onSetFilters({
          ...filters,
          [FilterKeys.StudentHouse]: studentIds ?? filters[FilterKeys.StudentHouse],
          [FilterKeys.StaffHouse]: staffIds ?? filters[FilterKeys.StaffHouse],
        });
      },
      onClearHouses: () => {
        onSetFilters({
          ...filters,
          [FilterKeys.StudentHouse]: [],
          [FilterKeys.StaffHouse]: [],
        });
      },
      onSetOnlyTutorGroups,
      onClearOnlyTutorGroups: () => {
        onSetFilters({
          ...filters,
          [FilterKeys.OnlyTutorGroups]: undefined,
        });
      },
    };
  }, [filters, onSetFilters]);

  const filtersDate = filters.date;
  const filtersAgeGroup = filters.age_group;
  const filtersSubject = filters.subject;
  const filtersStaff = filters.staff;
  const filtersStudentStatus = filters.student_status;
  const filtersStaffStatus = filters.staff_status;
  const filtersStudentHouse = filters.student_house;
  const filtersStaffHouse = filters.staff_house;
  const filtersOnlyTutorGroups = filters.only_tutor_groups;

  const labels = {
    [FilterKeys.Date]: formatMessage({ id: 'schoolProperty-Period' }),
    [FilterKeys.AgeGroup]: formatMessage({ id: 'schoolProperty-AgeGroup' }),
    [FilterKeys.Subject]: formatMessage({ id: 'groups-GroupSubject' }),
    [FilterKeys.Staff]: formatMessage({ id: 'schoolProperty-Staff' }),
    status: formatMessage({ id: 'schoolProperty-Status' }),
    house: formatMessage({ id: 'schoolProperty-House' }),
    onlyTutorGroups: formatMessage({ id: 'groups-OnlyTutorGroups' }),
    arrangeBy: formatMessage({ id: 'filter-ArrangeBy' }),
  };

  const arrangeByDropdownOptions = GROUPS_ARRANGE_BY_FILTER_KEYS;

  const selectedItemsForAgeGroups: SelectedItemWithGrouping[] = useMemo(() => {
    const selectedAgeGroups =
      filtersAgeGroup?.reduce<SelectedItem[]>((acc, id) => {
        const ageGroup = getAgeGroupById(id);

        return ageGroup
          ? [
              ...acc,
              {
                id: ageGroup.id,
                groupId: ageGroup.level_id,
              },
            ]
          : acc;
      }, []) ?? [];

    return getSelectedItemsWithGrouping(selectedAgeGroups, schoolLevelsWithAgeGroupsMap);
  }, [filtersAgeGroup, schoolLevelsWithAgeGroupsMap, getAgeGroupById]);

  return {
    ...ageGroups,
    ...actions,
    labels,
    hasHouses,
    hasSubjects,
    arrangeByDropdownOptions,
    selectedItemsForAgeGroups,
    filtersDate,
    filtersAgeGroup,
    filtersSubject,
    filtersStaff,
    filtersStudentStatus,
    filtersStaffStatus,
    filtersStudentHouse,
    filtersStaffHouse,
    filtersOnlyTutorGroups,
  };
};
